import { ENTITY_TYPES } from '@sales-center/common'
import React, { useCallback, useMemo, useState } from 'react'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import { DraggableAgreementList } from '@user/components/Agreements/AgreementFormPacketDialog/DraggableAgreementList'
import { useHandlers, useLeadAutoCloseIndicator } from './hooks'
import { LeadAutoCloseBanner } from './LeadAutoCloseBanner'
import {
  LeadAutoCloseMissingFieldsBanner,
  RequiredFieldsDialog,
} from '@user/components/LeadAutoClose'
import { useFormikContext } from 'formik'
import {
  CreateAgreementFormPacketModel,
  CreateAgreementTemplateDetails,
} from '@user/components/Agreements/AgreementFormPacketDialog/typings'
import { AgreementCreateFields } from '@user/components/Agreements/AgreementFormPacketDialog/AgreementCreateFields'
import { ConfirmationDialog, InfoTooltip } from '@sales-center/components'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import { useSelector } from 'react-redux'
import { selectAllLeadOppProducts } from '@user/store/leadOppProducts'
import AgreementAttachments from './AgreementAttachments'
import { useAgreementAttachmentEnabled } from '@user/store/tenantFeatures/hooks/useAgreementAttachmentEnabled'
import { selectAgreementConfigs } from '@user/store/agreements'
import useMediaQuery from '@mui/material/useMediaQuery'
import { SxProps, Theme, useTheme } from '@mui/material/styles'

interface Props {
  entityId: number
  entityType: ENTITY_TYPES
  openServicesDrawer: () => void
  onPreview: (model: CreateAgreementFormPacketModel) => void
  cancelPopupOpen: boolean
  handleCancel: () => void
  cancelPopupClose: () => void
}

export function AgreementFormPacket(props: Props) {
  const {
    entityId,
    entityType,
    openServicesDrawer,
    onPreview,
    cancelPopupOpen,
    handleCancel,
    cancelPopupClose,
  } = props
  const { submitForm, values, submitCount, isValid, setFormikState } =
    useFormikContext<CreateAgreementFormPacketModel>()
  const services = useSelector(selectAllLeadOppProducts)

  const agreementAttachmentEnabled = useAgreementAttachmentEnabled()
  const agreementConfigs = useSelector(selectAgreementConfigs)

  const { templates } = values
  const { onDragEnd } = useHandlers()
  const {
    showAutoCloseLeadBanner,
    showMissingFieldsBanner,
    showOppAutoCloseMessage,
    missingFieldIds,
    missingFieldCount,
  } = useLeadAutoCloseIndicator(entityId, entityType)

  const [requiredFieldsOpen, setRequiredFieldsOpen] = useState(false)

  const onOpenRequiredFields = useCallback(
    () => setRequiredFieldsOpen(true),
    [],
  )

  const onCloseRequiredFields = useCallback(
    () => setRequiredFieldsOpen(false),
    [],
  )
  const templateAdded = useMemo(() => {
    return templates.length > 0
  }, [templates.length])

  const templateWithServicesAdded = useMemo(
    () =>
      templates.some(
        (item: CreateAgreementTemplateDetails) =>
          'templateId' in item && (item.services?.length ?? 0) > 0,
      ),
    [templates],
  )
  const formSubmitted = submitCount > 0

  const cardCaptureOn = useMemo(
    () =>
      templates.some(
        (item: CreateAgreementTemplateDetails) => item.isCardCaptureEnabled,
      ),
    [templates],
  )

  const allServicesIncluded = useMemo(() => {
    const templateServiceIds = templates
      ?.filter((item) => 'templateId' in item)
      .flatMap(
        (template: CreateAgreementTemplateDetails) =>
          template.services?.map((sv) => sv.id) || [],
      )
    return (
      templates.length > 0 &&
      services.every((service) => templateServiceIds.includes(service.id))
    )
  }, [templates, services])

  const onCLickSubmit = useCallback(() => {
    if (allServicesIncluded && isValid) {
      submitForm()
    } else {
      setFormikState((prevState) => ({
        ...prevState,
        submitCount: prevState.submitCount + 1,
      }))
      cancelPopupClose()
    }
  }, [
    allServicesIncluded,
    cancelPopupClose,
    isValid,
    setFormikState,
    submitForm,
  ])

  const onCLickPreview = useCallback(() => {
    onPreview(values)
  }, [onPreview, values])

  const theme = useTheme()
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('xl'))

  const headerSx: SxProps<Theme> = isMobileOrTablet
    ? {
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: theme.palette.background.default,
        mt: 3,
        pt: 2,
        pb: 2,
      }
    : { mt: 5, mb: 2 }

  const handleSaveForLater = useCallback(() => {
    sessionStorage.setItem('SaveForLater', 'true')
    onCLickSubmit()
  }, [onCLickSubmit])

  return (
    <>
      <Stack maxWidth="746px" width="100%" mb={3}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={headerSx}
        >
          <Typography variant="h4">
            {agreementConfigs ? 'Update Agreement' : 'Create Agreement'}
          </Typography>

          {services.length > 0 && (
            <Stack direction="row" alignItems="center">
              <Button
                size="medium"
                variant="text"
                sx={{ height: 36, mr: 1, color: '#1565C0' }}
                disabled={!allServicesIncluded || !isValid}
                onClick={onCLickPreview}
                data-test-id="agreement-form-packet-preview-button"
              >
                Preview
              </Button>
              <Button
                size="medium"
                variant="contained"
                sx={{ height: 36 }}
                onClick={onCLickSubmit}
                disabled={!isValid}
                data-test-id="agreement-form-packet-launch-button"
              >
                Launch form to complete
              </Button>
            </Stack>
          )}
        </Stack>

        <Typography variant="body2">
          Build an Agreement using one or more Agreement Templates. Each service
          must be added to at least one template.
        </Typography>

        {services.length === 0 ? (
          <Card
            sx={{
              mt: '20px',
              p: 2,
              paddingY: '24px',
              width: `100%`,
              justifyContent: 'center',
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
            }}
            variant="outlined"
          >
            <Typography variant="h6">No services or products added.</Typography>
            <Typography variant="body1">
              You must add at least one service before you can create an
              agreement.
            </Typography>
            <Button
              sx={{ mt: '20px' }}
              size="large"
              variant="contained"
              onClick={openServicesDrawer}
              data-test-id="agreement-form-packet-add-service-button"
            >
              Add Service or Product
            </Button>
          </Card>
        ) : (
          <Card
            sx={{
              mt: '20px',
              p: 2,
              width: `100%`,
            }}
            variant="outlined"
          >
            <AgreementCreateFields
              entityId={entityId}
              entityType={entityType}
              openServicesDrawer={openServicesDrawer}
            />

            <Typography
              variant="h6"
              mb={showOppAutoCloseMessage || cardCaptureOn ? '12px' : 1.5}
            >
              Agreement
            </Typography>

            {(showOppAutoCloseMessage || cardCaptureOn) && (
              <Typography mb="12px" width="100%" variant="body2">
                {showOppAutoCloseMessage &&
                  'This record will automatically be closed/won and submitted to the Closed Won Queue after the customer signs and completes the agreement.'}
                {cardCaptureOn && (
                  <>
                    {showOppAutoCloseMessage && <>&nbsp;</>}
                    This agreement will prompt the customer to put their card on
                    file. &nbsp;
                    <InfoTooltip
                      fontSize={15}
                      style={{ marginTop: '-3px', color: '#0E283FD9' }}
                      infoText="Captured cards from forms will be stored on the Bill-to and visible in Sales Center under the Payments closing requirement."
                    />
                  </>
                )}
              </Typography>
            )}

            {templates?.filter((item) => 'templateId' in item).length > 1 && (
              <Box mt={1} mb="12px">
                <Alert severity="info">
                  <Typography variant="body2">
                    These templates will be combined into a single packet.
                  </Typography>
                </Alert>
              </Box>
            )}

            {agreementAttachmentEnabled ? (
              <AgreementAttachments
                entityType={entityType}
                entityId={entityId}
              />
            ) : null}

            {showAutoCloseLeadBanner && templateWithServicesAdded && (
              <LeadAutoCloseBanner />
            )}

            {showMissingFieldsBanner && templateWithServicesAdded && (
              <LeadAutoCloseMissingFieldsBanner
                count={missingFieldCount}
                onUpdateRequiredFields={onOpenRequiredFields}
              />
            )}

            {requiredFieldsOpen && (
              <RequiredFieldsDialog
                leadId={entityId}
                missingFieldIds={missingFieldIds}
                onClose={onCloseRequiredFields}
              />
            )}

            {templateAdded ? (
              <DraggableAgreementList
                onDragEnd={onDragEnd}
                entityId={entityId}
                entityType={entityType}
                openServicesDrawer={openServicesDrawer}
              />
            ) : (
              <>
                <Typography
                  variant="body1"
                  sx={{
                    color: (theme) =>
                      formSubmitted
                        ? theme.palette.error.main
                        : theme.palette.text.primary,
                    width: '100%',
                    height: '200px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px',
                    backgroundColor: (theme) =>
                      theme.palette.background.default,
                    boxShadow: '0px 0px 0px 1px #E0E0E0',
                    border: formSubmitted ? '1px solid' : 'none',
                    borderColor: (theme) => theme.palette.error.main,
                  }}
                >
                  0 Templates Added
                </Typography>

                {formSubmitted && !templateAdded && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: (theme) => theme.palette.error.dark,
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                    }}
                  >
                    At least one agreement required
                  </Typography>
                )}
              </>
            )}
          </Card>
        )}
      </Stack>

      {cancelPopupOpen && (
        <ConfirmationDialog
          cancelClick={templateAdded ? handleCancel : cancelPopupClose}
          message={
            templateAdded
              ? agreementConfigs
                ? 'Closing will discard any changes you have made to the agreement. Any quoted services will be saved.'
                : 'You can choose to save this agreement as a draft or discard it. Any quoted services will be saved.'
              : "You haven't selected a template so this agreement will be deleted. Any quoted services will be saved."
          }
          okButtonText={templateAdded ? 'Save for later' : 'Continue to close'}
          cancelButtonText={templateAdded ? 'Discard and close' : 'Cancel'}
          okClick={templateAdded ? handleSaveForLater : handleCancel}
          closeIconClick={templateAdded ? cancelPopupClose : undefined}
          open={cancelPopupOpen}
          title={
            !templateAdded
              ? 'No Agreement Templates Selected'
              : 'Close Agreement'
          }
        />
      )}
    </>
  )
}
